import { useEffect, useState } from "react";
import { Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const OFFICE = /word|excel|powerpoint|officedocument|msword|ms-excel|ms-powerpoint/;

export function PreviewDialog({ doc, onClose }) {
  const [src, setSrc] = useState(null);
  const [kind, setKind] = useState("pdf");
  useEffect(() => {
    if (!doc) { setSrc(null); return; }
    let revoke;
    (async () => {
      try {
        const ct = doc.content_type || "";
        if (ct === "application/pdf" || ct.startsWith("image/") || ct.startsWith("text/")) {
          const r = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
          const url = URL.createObjectURL(new Blob([r.data], { type: ct })); revoke = url;
          setKind(ct.startsWith("image/") ? "image" : ct.startsWith("text/") ? "text" : "pdf"); setSrc(url);
        } else if (OFFICE.test(ct)) {
          const r = await api.post(`/documents/${doc.id}/preview-token`);
          setKind("office"); setSrc(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(r.data.url)}`);
        } else { setKind("none"); setSrc("x"); }
      } catch (e) { toast.error(errMsg(e)); onClose(); }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [doc]); // eslint-disable-line
  if (!doc) return null;
  const download = async () => { const r = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = doc.original_filename; a.click(); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="panel max-w-6xl w-[95vw] h-[90vh] flex flex-col p-4" data-testid="preview-dialog">
        <DialogHeader className="flex-row items-center justify-between space-y-0 pr-8">
          <div><DialogTitle className="font-serif text-xl text-white">{doc.document_name}</DialogTitle><DialogDescription className="text-xs text-slate-400">{doc.original_filename}{kind === "office" ? " · rendered with Microsoft Office viewer (15-minute secure link)" : ""}</DialogDescription></div>
          <div className="flex gap-2"><Button size="sm" variant="outline" onClick={download} data-testid="preview-download-button"><Download className="w-4 h-4 mr-1" /> Download</Button>{src && kind !== "none" && <Button size="sm" variant="ghost" onClick={() => window.open(src, "_blank", "noopener")} data-testid="preview-open-tab-button"><ExternalLink className="w-4 h-4" /></Button>}</div>
        </DialogHeader>
        <div className="flex-1 min-h-0 rounded-md overflow-hidden bg-[#0B132B] border border-[#2E3B61]/60 flex items-center justify-center" data-testid="preview-body">
          {!src ? <p className="text-slate-400 text-sm">Loading preview…</p>
            : kind === "image" ? <img src={src} alt={doc.document_name} className="max-h-full max-w-full object-contain" data-testid="preview-image" />
            : kind === "none" ? <div className="text-center text-slate-400 text-sm p-8">No inline preview available for this file type.<br />Use Download to open it on your device.</div>
            : <iframe title={doc.document_name} src={src} className="w-full h-full bg-white" data-testid="preview-iframe" />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
